import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  FetchSchoolCalendarListParams,
  SchoolCalendar
} from 'Services/SchoolManagement/Features/SchoolCalendar/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface SchoolCalendarListProps {
  rows: SchoolCalendar[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedSchoolCalendarId: string | undefined
  params: FetchSchoolCalendarListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
}

export default function SchoolCalendarList({
  rows,
  isLoading,
  onRowClick,
  selectedSchoolCalendarId,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page
}: Readonly<SchoolCalendarListProps>) {
  const headCells: HeadCell[] = [
    {
      id: 'calendarId',
      numeric: false,
      disablePadding: false,
      label: t`Calendar identifier`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Name`,
      sortable: true
    },
    {
      id: 'year',
      numeric: false,
      disablePadding: false,
      label: t`Year`,
      sortable: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={row.id === selectedSchoolCalendarId}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {row.calendar_id}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {row.name}
                    </ClickableTableCell>
                    <NoWrapTableCell component="th" scope="row">
                      {showValueOrHyphen(row.year)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
